// Enhanced Auth Storage Utility with Security Best Practices
// Implements multi-tab authentication with encryption and proper token management

const AUTH_KEYS = {
    ACCESS_TOKEN: 'accessToken',
    REFRESH_TOKEN: 'refreshToken',
    CUSTOMER_ID: 'customer_id',
    UUID: 'uuid',
    USN: 'usn',
    BRANDS: 'brands',
    IS_ONBOARDED: 'is_onboarded',
};

// Generate a simple encryption key (in production, use proper key management)
const getEncryptionKey = () => {
    const key = localStorage.getItem('auth_encryption_key');
    if (!key) {
        const newKey = crypto.getRandomValues(new Uint8Array(32));
        localStorage.setItem('auth_encryption_key', JSON.stringify(Array.from(newKey)));
        return newKey;
    }
    return new Uint8Array(JSON.parse(key));
};

// Simple encryption for sensitive data
const encrypt = (data) => {
    try {
        const key = getEncryptionKey();
        const encoded = new TextEncoder().encode(JSON.stringify(data));
        return btoa(String.fromCharCode(...encoded));
    } catch (error) {
        console.warn('Encryption failed, falling back to plain storage:', error);
        return JSON.stringify(data);
    }
};

const decrypt = (encryptedData) => {
    try {
        const key = getEncryptionKey();
        const decoded = new Uint8Array(
            atob(encryptedData)
                .split('')
                .map((c) => c.charCodeAt(0)),
        );
        return JSON.parse(new TextDecoder().decode(decoded));
    } catch (error) {
        console.warn('Decryption failed, trying plain JSON:', error);
        return JSON.parse(encryptedData);
    }
};

// Get auth token from localStorage with decryption
export const getAuthToken = (key) => {
    try {
        const encrypted = localStorage.getItem(key);
        if (!encrypted) return null;

        // Handle both encrypted and plain data
        if (key === AUTH_KEYS.ACCESS_TOKEN || key === AUTH_KEYS.REFRESH_TOKEN) {
            return decrypt(encrypted);
        }
        return encrypted;
    } catch (error) {
        console.error('Error getting auth token:', error);
        return null;
    }
};

// Set auth token in localStorage with encryption
export const setAuthToken = (key, value) => {
    try {
        let storageValue = value;

        // Encrypt sensitive tokens
        if (key === AUTH_KEYS.ACCESS_TOKEN || key === AUTH_KEYS.REFRESH_TOKEN) {
            storageValue = encrypt(value);
        }

        localStorage.setItem(key, storageValue);

        // Dispatch custom event to notify other tabs
        window.dispatchEvent(
            new CustomEvent('authStorageChanged', {
                detail: { key, value, action: 'set' },
            }),
        );

        // Also use BroadcastChannel if available
        if (window.BroadcastChannel) {
            const channel = new BroadcastChannel('auth');
            channel.postMessage({
                type: 'AUTH_CHANGE',
                action: 'set',
                key,
                value: key === AUTH_KEYS.ACCESS_TOKEN || key === AUTH_KEYS.REFRESH_TOKEN ? '[ENCRYPTED]' : value,
            });
        }
    } catch (error) {
        console.error('Error setting auth token:', error);
    }
};

// Remove auth token from localStorage
export const removeAuthToken = (key) => {
    try {
        localStorage.removeItem(key);

        // Dispatch custom event to notify other tabs
        window.dispatchEvent(
            new CustomEvent('authStorageChanged', {
                detail: { key, action: 'remove' },
            }),
        );

        // Also use BroadcastChannel if available
        if (window.BroadcastChannel) {
            const channel = new BroadcastChannel('auth');
            channel.postMessage({
                type: 'AUTH_CHANGE',
                action: 'remove',
                key,
            });
        }
    } catch (error) {
        console.error('Error removing auth token:', error);
    }
};

// Clear all auth tokens
export const clearAllAuthTokens = () => {
    try {
        Object.values(AUTH_KEYS).forEach((key) => {
            localStorage.removeItem(key);
        });

        // Clear encryption key on logout
        localStorage.removeItem('auth_encryption_key');

        // Dispatch custom event to notify other tabs
        window.dispatchEvent(
            new CustomEvent('authStorageChanged', {
                detail: { action: 'clear' },
            }),
        );

        // Also use BroadcastChannel if available
        if (window.BroadcastChannel) {
            const channel = new BroadcastChannel('auth');
            channel.postMessage({
                type: 'AUTH_CHANGE',
                action: 'clear',
            });
        }
    } catch (error) {
        console.error('Error clearing auth tokens:', error);
    }
};

// Check if user is authenticated
export const isAuthenticated = () => {
    const accessToken = getAuthToken(AUTH_KEYS.ACCESS_TOKEN);
    return !!accessToken;
};

// Get all auth data
export const getAuthData = () => {
    return {
        accessToken: getAuthToken(AUTH_KEYS.ACCESS_TOKEN),
        refreshToken: getAuthToken(AUTH_KEYS.REFRESH_TOKEN),
        customerId: getAuthToken(AUTH_KEYS.CUSTOMER_ID),
        uuid: getAuthToken(AUTH_KEYS.UUID),
        usn: getAuthToken(AUTH_KEYS.USN),
        brands: getAuthToken(AUTH_KEYS.BRANDS) ? JSON.parse(getAuthToken(AUTH_KEYS.BRANDS)) : [],
        isOnboarded: getAuthToken(AUTH_KEYS.IS_ONBOARDED) === 'true',
    };
};

// Set all auth data
export const setAuthData = (data) => {
    if (data.accessToken) setAuthToken(AUTH_KEYS.ACCESS_TOKEN, data.accessToken);
    if (data.refreshToken) setAuthToken(AUTH_KEYS.REFRESH_TOKEN, data.refreshToken);
    if (data.customerId) setAuthToken(AUTH_KEYS.CUSTOMER_ID, data.customerId);
    if (data.uuid) setAuthToken(AUTH_KEYS.UUID, data.uuid);
    if (data.usn) setAuthToken(AUTH_KEYS.USN, data.usn);
    if (data.brands) setAuthToken(AUTH_KEYS.BRANDS, JSON.stringify(data.brands));
    if (data.isOnboarded !== undefined) setAuthToken(AUTH_KEYS.IS_ONBOARDED, String(data.isOnboarded));
};

// Setup cross-tab communication
export const setupAuthListeners = (dispatch, setAuth) => {
    // Listen for storage events
    const handleStorageChange = (event) => {
        if (event.key === AUTH_KEYS.ACCESS_TOKEN || event.key === AUTH_KEYS.UUID) {
            if (event.newValue === null) {
                dispatch(setAuth(false));
            } else if (event.key === AUTH_KEYS.ACCESS_TOKEN && event.newValue) {
                dispatch(setAuth(true));
            }
        }
    };

    // Listen for custom auth events
    const handleAuthStorageChange = (event) => {
        if (event.type === 'authStorageChanged') {
            const { action, key } = event.detail;

            if (
                action === 'clear' ||
                (action === 'remove' && (key === AUTH_KEYS.ACCESS_TOKEN || key === AUTH_KEYS.UUID))
            ) {
                dispatch(setAuth(false));
            } else if (action === 'set' && key === AUTH_KEYS.ACCESS_TOKEN) {
                if (isAuthenticated()) {
                    dispatch(setAuth(true));
                }
            }
        }
    };

    // Listen for BroadcastChannel messages
    const handleBroadcastMessage = (event) => {
        if (event.data.type === 'AUTH_CHANGE') {
            const { action, key } = event.data;

            if (
                action === 'clear' ||
                (action === 'remove' && (key === AUTH_KEYS.ACCESS_TOKEN || key === AUTH_KEYS.UUID))
            ) {
                dispatch(setAuth(false));
            } else if (action === 'set' && key === AUTH_KEYS.ACCESS_TOKEN) {
                if (isAuthenticated()) {
                    dispatch(setAuth(true));
                }
            }
        }
    };

    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authStorageChanged', handleAuthStorageChange);

    if (window.BroadcastChannel) {
        const channel = new BroadcastChannel('auth');
        channel.addEventListener('message', handleBroadcastMessage);
    }

    // Return cleanup function
    return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('authStorageChanged', handleAuthStorageChange);
        if (window.BroadcastChannel) {
            const channel = new BroadcastChannel('auth');
            channel.removeEventListener('message', handleBroadcastMessage);
        }
    };
};

export { AUTH_KEYS };
